import type { SupWeb3Balance } from "@/types";
import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { type Address, formatUnits } from "viem";
import { SupCheckCircle, SupX, SupXCircle } from "@repo/core-ui";
import { SupButton, SupLoader } from "@repo/core-ui";
import { checkIfRejected, classNames, depositTransferError, getWeb3Address, notifyMessage } from "@/utils";
import useChainContractConfig from "@/hooks/data/useChainContractConfig";
import dappConfig from "@/../config/dapp.config";
import { useWriteContract, useWaitForTransactionReceipt, useSimulateContract } from "wagmi";
import { getERC20Abi } from "@/abis/erc20";

interface SupWeb3DepositStepApprovalERC20Props {
  calculatedRoutes: Web3TxMultiCalcSWR;
  setApprovalRequired: Dispatch<SetStateAction<boolean>>;
  selectedBalance: SupWeb3Balance;
  approvedAmount: bigint | undefined;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
  contractAddress?: Address;
}

export default function Web3DepositApprovalERC20({
  calculatedRoutes,
  setApprovalRequired,
  selectedBalance,
  setPauseRequests,
  disabled = false,
  contractAddress,
}: SupWeb3DepositStepApprovalERC20Props) {
  const superRouterContract = useChainContractConfig(selectedBalance.chainId as number);
  const approveContract = useMemo(() => {
    return contractAddress || superRouterContract?.address;
  }, [contractAddress, superRouterContract]);

  const { totalBigIntAmount, totalFormatted } = useMemo(() => {
    const rawBigIntAmount =
      calculatedRoutes.data?.reduce((acc, singleCalculate) => (singleCalculate.in.adjusted_amount ? acc + BigInt(`${singleCalculate.in.adjusted_amount}`) : acc), 0n) ?? 0n;
    return {
      totalBigIntAmount: rawBigIntAmount,
      totalFormatted: parseFloat(formatUnits(rawBigIntAmount, selectedBalance.decimals)).toFixed(dappConfig.decimalsBigRoundingLimit),
    };
  }, [calculatedRoutes.data, selectedBalance.decimals]);

  const defaultButtonText = `Approve spending ${totalFormatted} ${selectedBalance.symbol}`;
  const toastId = useMemo(() => `${totalFormatted}_${selectedBalance.address}_${selectedBalance.chainId}`, [totalFormatted, selectedBalance.address, selectedBalance.chainId]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined | null>(undefined);
  const [buttonText, setButtonText] = useState<string>(defaultButtonText);

  const {
    data: config,
    error: prepareError,
    isLoading: isPrepareLoading,
  } = useSimulateContract({
    abi: getERC20Abi(getWeb3Address(selectedBalance.address), selectedBalance.chainId),
    chainId: selectedBalance.chainId as number,
    address: approveContract && getWeb3Address(selectedBalance.address),
    functionName: "approve",
    args: [approveContract as Address, totalBigIntAmount],
  });

  const { data, error: sendError, isPending: isSendLoading, writeContract } = useWriteContract();

  const {
    isLoading: isWaitLoading,
    isSuccess: isWaitSuccess,
    error: waitError,
  } = useWaitForTransactionReceipt({
    hash: data,
  });

  // Setting the loading state
  useEffect(() => {
    if (isPrepareLoading) {
      setButtonText("Preparing tx...");
    }
    if (isSendLoading) {
      setButtonText("Signing tx... (check the wallet)");
    }
    if (isWaitLoading) {
      setButtonText("Processing tx...");
    }
    if (error) {
      checkIfRejected(error) ? setButtonText(defaultButtonText) : setButtonText("Can't do approval");
    } else {
      if (!isLoading) {
        setButtonText(defaultButtonText);
      }
    }
  }, [isPrepareLoading, isSendLoading, isWaitLoading, error, isLoading, defaultButtonText]);

  useEffect(() => {
    setIsLoading(isPrepareLoading || isSendLoading || isWaitLoading);
  }, [isWaitLoading, isSendLoading, isPrepareLoading]);

  useEffect(() => {
    if (isWaitSuccess) {
      setIsLoading(false);
      setPauseRequests(false);
      notifyMessage("Token Approval", `Token spent successfully approved for ${totalFormatted}`, "success", <SupCheckCircle />, toastId);
      setApprovalRequired(false);
    }
  }, [isWaitSuccess, setApprovalRequired, setPauseRequests, toastId, totalFormatted]);

  useEffect(() => {
    if (error) {
      if (checkIfRejected(error)) {
        notifyMessage("Token Approval", `Transaction rejected`, "warning", <SupXCircle />, toastId);
        setError(undefined);
      } else {
        notifyMessage("Token Approval error", depositTransferError(error), "error", <SupXCircle />, toastId, 0);
      }
      setIsLoading(false);
      setPauseRequests(false);
    }
  }, [error, setPauseRequests, toastId]);

  useEffect(() => {
    setError(prepareError || sendError || waitError);
  }, [waitError, sendError, prepareError]);

  useEffect(() => {
    if (data) {
      notifyMessage("Token Approval", `Processing TX #${data}`, "primary", <SupLoader />, toastId);
    }
  }, [data, toastId, selectedBalance.address]);

  const callApprove = useCallback(() => {
    try {
      setPauseRequests(true);
      setIsLoading(true);
      config?.request && writeContract?.(config.request);
    } catch (e) {
      notifyMessage("Token Approval error", depositTransferError(e as Error), "error", <SupXCircle />, toastId, 0);
      setIsLoading(false);
      setPauseRequests(false);
    }
  }, [config?.request, setPauseRequests, toastId, writeContract]);

  const prefixIcon = isLoading ? <SupLoader isSmall color="primary" className="text-neutral-700" svgClassName="stroke-2" /> : !config?.request ? <SupX /> : undefined;

  /**
   * Web3 transaction awaiting
   */
  return (
    <SupButton
      fluid
      prefixIcon={prefixIcon}
      disabled={!config?.request || isLoading || disabled || !!error}
      className={classNames("w-full justify-center", (!config?.request || isLoading || disabled) && "cursor-not-allowed")}
      color={!config?.request && !isLoading ? "error" : "primary"}
      onClick={callApprove}
    >
      {buttonText}
    </SupButton>
  );
}
